/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-expressions, prefer-spread, prefer-rest-params */
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { BadgeCheck, Check, Globe } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { renderPlatformIcon, platformBrandColor } from '@/components/linktree/BlockRenderer';
import { getTemplate, getArtistTemplate, fontFamily } from '@/lib/linktree/templates';
import { trackPageView, trackLinkClick } from '@/lib/linktree/track';
import { safeUrl, safeCss, safeIframeSrc } from '@/lib/linktree/sanitize';
import { firePixelEvents, gtmDataLayerName } from '@/lib/linktree/pixels';
import { hexToRgba } from '@/lib/linktree/colors';
import type { LinkTreePage, LinkTreeBlock, LinkTreeTheme, LinkTreePixel } from '@/lib/linktree/types';

const PIXEL_DEDUP_LIMIT = 64;
const initializedPixels: Set<string> = new Set();

function markPixelInitialized(key: string) {
  if (initializedPixels.size >= PIXEL_DEDUP_LIMIT) {
    const oldest = initializedPixels.values().next().value;
    if (oldest !== undefined) initializedPixels.delete(oldest);
  }
  initializedPixels.add(key);
}

function normalizePlatform(platform: string | undefined | null): string {
  const key = String(platform || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const compact = key.replace(/_/g, '');
  const aliases: Record<string, string> = {
    twitter: 'x',
    x_twitter: 'x',
    apple: 'applemusic',
    apple_music: 'applemusic',
    applemusic: 'applemusic',
    apple_music_embed: 'applemusic',
    yt_music: 'youtubemusic',
    youtube_music: 'youtubemusic',
    youtubemusic: 'youtubemusic',
    amazon_music: 'amazonmusic',
    amazonmusic: 'amazonmusic',
  };
  return aliases[key] || aliases[compact] || compact || 'link';
}

function shadowFor(level: LinkTreeTheme['buttonShadow'], color: string): string {
  switch (level) {
    case 'none': return 'none';
    case 'soft': return `0 8px 24px -16px ${hexToRgba(color, 0.5)}`;
    case 'strong': return `0 18px 42px -18px ${hexToRgba(color, 0.72)}`;
    case 'hard': return '0 6px 0 rgba(0,0,0,0.72)';
    default: return `0 18px 42px -18px ${hexToRgba(color, 0.62)}`;
  }
}

function themedButtonStyle(theme: LinkTreeTheme): CSSProperties {
  const radius = theme.buttonRadius ?? 9999;
  const primary = theme.buttonColor && theme.buttonColor !== 'inherit' ? theme.buttonColor : theme.primary;
  const text = theme.buttonTextColor && theme.buttonTextColor !== 'inherit' ? theme.buttonTextColor : theme.text;
  const cardBg = theme.cardBg || 'rgba(255,255,255,0.10)';
  const cardBorder = theme.cardBorder || 'rgba(255,255,255,0.18)';
  const shadow = shadowFor(theme.buttonShadow, primary || theme.primary || '#ffffff');
  const base: CSSProperties = {
    borderRadius: radius,
    color: text,
    boxShadow: shadow,
    border: `1px solid ${cardBorder}`,
    transition: 'transform 160ms ease, border-color 160ms ease, background 160ms ease, opacity 160ms ease',
  };

  switch (theme.buttonStyle) {
    case 'solid':
      return { ...base, background: primary || cardBg };
    case 'outline':
      return { ...base, background: 'transparent', border: `1.5px solid ${cardBorder || primary}` };
    case 'soft':
      return {
        ...base,
        background: `linear-gradient(135deg, ${hexToRgba(primary, 0.22)} 0%, ${cardBg} 100%)`,
      };
    case 'glass':
    default:
      return {
        ...base,
        background: cardBg,
        backdropFilter: 'blur(16px) saturate(150%)',
        WebkitBackdropFilter: 'blur(16px) saturate(150%)',
        boxShadow: shadow === 'none' ? 'inset 0 1px 0 rgba(255,255,255,0.12)' : `${shadow}, inset 0 1px 0 rgba(255,255,255,0.12)`,
      };
  }
}

function styleTheme(baseTheme: LinkTreeTheme, block: LinkTreeBlock): LinkTreeTheme {
  const style = (block.data?.style || {}) as Record<string, any>;
  return {
    ...baseTheme,
    ...(style.bgColor ? { cardBg: style.bgColor, buttonColor: style.bgColor } : {}),
    ...(style.textColor ? { text: style.textColor, buttonTextColor: style.textColor } : {}),
    ...(style.buttonRadius != null ? { buttonRadius: Number(style.buttonRadius) } : {}),
    ...(style.buttonStyle ? { buttonStyle: style.buttonStyle } : {}),
  };
}

function blockFont(theme: LinkTreeTheme, block: LinkTreeBlock): CSSProperties {
  const style = (block.data?.style || {}) as Record<string, any>;
  return {
    fontFamily: style.fontFamily ? `'${style.fontFamily}', ${fontFamily(theme.font)}` : fontFamily(theme.font),
    fontWeight: style.fontWeight,
    fontStyle: style.fontItalic ? 'italic' : undefined,
  };
}

function Outbound({ href, disabled, onClick, style, ariaLabel, children }: {
  href?: string | null;
  disabled?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
  ariaLabel?: string;
  children: ReactNode;
}) {
  const safe = !disabled ? safeUrl(href) : null;
  if (!safe) {
    return (
      <button
        type="button"
        disabled={disabled ?? true}
        aria-disabled="true"
        aria-label={ariaLabel}
        style={{ ...style, cursor: 'default', opacity: style?.opacity }}
      >
        {children}
      </button>
    );
  }
  return (
    <a
      href={safe}
      target="_blank"
      rel="noopener noreferrer nofollow ugc"
      aria-label={ariaLabel}
      onClick={onClick}
      style={{ ...style, textDecoration: 'none' }}
    >
      {children}
    </a>
  );
}

function PlatformGlyph({ platform, size = 18, color }: { platform?: string; size?: number; color: string }) {
  const key = normalizePlatform(platform);
  return renderPlatformIcon(key, size, color) || <Globe size={size} color={color} style={{ display: 'block' }} />;
}

function HandlePill({ block, theme, onLinkClick }: { block: LinkTreeBlock; theme: LinkTreeTheme; onLinkClick?: (block: LinkTreeBlock, url: string) => void }) {
  const d = block.data || {};
  const platform = normalizePlatform(d.platform || d.iconPlatform || 'instagram');
  const brand = platformBrandColor(platform);
  const iconColor = theme.linkIconColor || (d.iconMono ? theme.text : (brand && brand !== '#ffffff' ? brand : theme.text));
  const label = d.handle || d.label || '@username';
  const href = safeUrl(d.url);

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Outbound
        href={href}
        disabled={!href}
        onClick={() => href && onLinkClick?.(block, href)}
        ariaLabel={`${platform} ${label}`}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          minHeight: 34, padding: '8px 15px', borderRadius: 9999,
          background: theme.cardBg || 'rgba(255,255,255,0.10)',
          border: `1px solid ${theme.cardBorder || 'rgba(255,255,255,0.18)'}`,
          color: theme.text, fontSize: 13, fontWeight: 600,
          backdropFilter: 'blur(14px) saturate(150%)', WebkitBackdropFilter: 'blur(14px) saturate(150%)',
          boxShadow: shadowFor(theme.buttonShadow, theme.primary),
        }}
      >
        <span style={{ display: 'inline-flex', lineHeight: 0, color: iconColor }}><PlatformGlyph platform={platform} size={14} color={iconColor} /></span>
        <span>{label}</span>
      </Outbound>
    </div>
  );
}

function SocialRow({ block, theme, onLinkClick }: { block: LinkTreeBlock; theme: LinkTreeTheme; onLinkClick?: (block: LinkTreeBlock, url: string) => void }) {
  const items = ((block.data?.items as any[]) || []).filter(item => item?.url);
  if (items.length === 0) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 10 }}>
      {items.map((item, index) => {
        const platform = normalizePlatform(item.platform);
        const brand = platformBrandColor(platform);
        const iconColor = theme.linkIconColor || (item.iconMono ? theme.text : (brand && brand !== '#ffffff' ? brand : theme.text));
        const href = safeUrl(item.url);
        return (
          <Outbound
            key={`${platform}-${index}`}
            href={href}
            disabled={!href}
            onClick={() => href && onLinkClick?.(block, href)}
            ariaLabel={item.platform || platform}
            style={{
              width: 38, height: 38, borderRadius: '50%', padding: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: theme.cardBg || 'rgba(255,255,255,0.10)',
              border: `1px solid ${theme.cardBorder || 'rgba(255,255,255,0.16)'}`,
              color: iconColor,
              backdropFilter: 'blur(14px) saturate(150%)', WebkitBackdropFilter: 'blur(14px) saturate(150%)',
            }}
          >
            <PlatformGlyph platform={platform} size={17} color={iconColor} />
          </Outbound>
        );
      })}
    </div>
  );
}


function embedId(url: string, provider: 'spotify' | 'youtube') {
  if (provider === 'spotify') return url.match(/spotify\.com\/(track|album|playlist|artist)\/([a-zA-Z0-9]+)/);
  return url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
}

function Placeholder({ theme, label }: { theme: LinkTreeTheme; label: string }) {
  return (
    <div style={{
      minHeight: 86, borderRadius: 16, display: 'grid', placeItems: 'center',
      background: theme.cardBg || 'rgba(255,255,255,0.08)',
      border: `1px dashed ${theme.cardBorder || 'rgba(255,255,255,0.22)'}`,
      color: theme.text, opacity: 0.74, fontSize: 13,
    }}>{label}</div>
  );
}

function PublicBlock({ block, theme, onLinkClick }: { block: LinkTreeBlock; theme: LinkTreeTheme; onLinkClick?: (block: LinkTreeBlock, url: string) => void }) {
  const d = block.data || {};
  const t = styleTheme(theme, block);
  const font = blockFont(t, block);

  switch (block.type) {
    case 'link': {
      const href = safeUrl(d.url);
      const button = themedButtonStyle(t);
      const platform = normalizePlatform(d.iconPlatform || d.platform);
      const brand = platformBrandColor(platform);
      const iconColor = t.linkIconColor || (d.iconMono ? t.text : (brand && brand !== '#ffffff' ? brand : t.text));
      const thumb = safeUrl(d.thumbnail || d.image);
      return (
        <Outbound
          href={href}
          disabled={!href}
          onClick={() => href && onLinkClick?.(block, href)}
          ariaLabel={d.label || d.title || 'Open link'}
          style={{
            ...button, ...font,
            width: '100%', minHeight: 50, padding: '0 18px', border: button.border,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
            position: 'relative', boxSizing: 'border-box', fontSize: 14, fontWeight: (font.fontWeight as number) || 700,
          }}
        >
          {thumb ? (
            <img src={thumb} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', left: 7, width: 36, height: 36, borderRadius: Math.min(Number(t.buttonRadius) || 18, 18), objectFit: 'cover' }} />
          ) : (d.iconPlatform || d.platform) ? (
            <span style={{ display: 'inline-flex', lineHeight: 0, color: iconColor }}><PlatformGlyph platform={platform} size={18} color={iconColor} /></span>
          ) : d.icon ? <span aria-hidden style={{ fontSize: 15 }}>{d.icon}</span> : null}
          <span>{d.label || d.title || 'Click here'}</span>
        </Outbound>
      );
    }
    case 'handle':
      return <HandlePill block={block} theme={t} onLinkClick={onLinkClick} />;
    case 'social_row':
      return <SocialRow block={block} theme={t} onLinkClick={onLinkClick} />;
    case 'bio':
      return <p style={{ color: t.text, opacity: 0.84, fontSize: 14, lineHeight: 1.55, textAlign: 'center', margin: 0, ...font }}>{d.text}</p>;
    case 'text': {
      const sizes: Record<string, number> = { sm: 13, md: 15, lg: 20, xl: 28 };
      const size = sizes[d.size || 'md'] || 15;
      const bg = d.style?.bgColor;
      return (
        <div style={{ textAlign: 'center' }}>
          <p style={{
            color: d.style?.textColor || t.text, fontSize: size, lineHeight: 1.45, margin: 0,
            display: bg ? 'inline-block' : 'block', background: bg, borderRadius: bg ? 9999 : undefined,
            padding: bg ? '9px 16px' : undefined, ...font,
            fontWeight: d.style?.fontWeight ?? (d.size === 'lg' ? 700 : font.fontWeight),
          }}>{d.text}</p>
        </div>
      );
    }
    case 'divider':
      return <hr style={{ border: 0, borderTop: `1px solid ${d.color || t.dividerColor || `${t.text}44`}`, margin: '2px 0' }} />;
    case 'spacer':
      return <div style={{ height: Number(d.size) || 24 }} />;
    case 'spotify_embed': {
      const match = embedId(d.url || '', 'spotify');
      if (!match) return <Placeholder theme={t} label="Add a Spotify URL" />;
      return <iframe src={`https://open.spotify.com/embed/${match[1]}/${match[2]}`} title="Spotify player" loading="lazy" width="100%" height="152" frameBorder="0" allow="encrypted-media" style={{ borderRadius: 16, display: 'block', boxShadow: shadowFor(t.buttonShadow, t.primary) }} />;
    }
    case 'youtube_embed': {
      const match = embedId(d.url || '', 'youtube');
      if (!match) return <Placeholder theme={t} label="Add a YouTube URL" />;
      const urlStartMatch = String(d.url || '').match(/[?&](?:t|start)=(\d+)/);
      const rawStart = String(d.startTime ?? '').trim();
      let startField = 0;
      if (/^\d+$/.test(rawStart)) startField = parseInt(rawStart, 10) || 0;
      else if (/^\d+:\d{1,2}$/.test(rawStart)) {
        const [m, s] = rawStart.split(':').map(n => parseInt(n, 10) || 0);
        startField = m * 60 + s;
      }
      const startSec = startField > 0 ? startField : (urlStartMatch ? parseInt(urlStartMatch[1], 10) : 0);
      const params: string[] = [];
      if (startSec > 0) params.push(`start=${startSec}`);
      if (d.autoplay) { params.push('autoplay=1', 'mute=1', 'playsinline=1'); }
      else if (d.muted) params.push('mute=1');
      if (d.hideChrome) params.push('controls=0', 'modestbranding=1', 'rel=0', 'showinfo=0', 'iv_load_policy=3', 'disablekb=1', 'fs=0', 'cc_load_policy=0', 'color=white');
      const qs = params.length ? `?${params.join('&')}` : '';
      const maskColor = (t as any).cardBg || t.bg || '#0a0a0a';
      return <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 16, overflow: 'hidden', border: `1px solid ${t.cardBorder || 'rgba(255,255,255,0.16)'}` }}>
        <iframe key={qs} src={`https://www.youtube-nocookie.com/embed/${match[1]}${qs}`} title="YouTube video" loading="lazy" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
        {d.hideChrome && (<>
          <div aria-hidden style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 56, background: maskColor, pointerEvents: 'none' }} />
          <div aria-hidden style={{ position: 'absolute', bottom: 0, right: 0, width: 110, height: 44, background: maskColor, pointerEvents: 'none' }} />
        </>)}
      </div>;
    }
    case 'soundcloud_embed': {
      const href = safeIframeSrc(d.url);
      if (!href) return <Placeholder theme={t} label="Add a SoundCloud URL" />;
      return <iframe width="100%" height="120" frameBorder="0" title="SoundCloud player" loading="lazy" src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(href)}&color=%23${(t.primary || '#ffffff').replace('#', '')}&inverse=true&auto_play=false`} style={{ borderRadius: 16, display: 'block' }} />;
    }
    case 'apple_music_embed': {
      const embedUrl = String(d.url || '').replace(/^https?:\/\/(?:geo\.)?music\.apple\.com/i, 'https://embed.music.apple.com');
      if (!/^https:\/\/embed\.music\.apple\.com/i.test(embedUrl)) return <Placeholder theme={t} label="Add an Apple Music URL" />;
      return <iframe src={embedUrl} title="Apple Music player" loading="lazy" allow="autoplay *; encrypted-media *;" sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" width="100%" height="175" frameBorder="0" style={{ borderRadius: 16, display: 'block', background: 'transparent' }} />;
    }
    case 'tour_dates': {
      const dates = ((d.dates as any[]) || []).filter(date => date?.city || date?.venue || date?.date);
      if (dates.length === 0) return null;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {dates.map((date, index) => {
            const href = safeUrl(date.url);
            return (
              <Outbound
                key={`${date.city || 'date'}-${index}`}
                href={href}
                disabled={!href}
                onClick={() => href && onLinkClick?.(block, href)}
                ariaLabel={[date.city, date.venue, date.date].filter(Boolean).join(' · ') || 'Tour date'}
                style={{
                  ...themedButtonStyle(t), ...font,
                  width: '100%', minHeight: 48, padding: '12px 15px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
                  boxSizing: 'border-box', fontSize: 13, fontWeight: 700,
                }}
              >
                <span style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[date.city, date.venue].filter(Boolean).join(' · ')}</span>
                <span style={{ opacity: 0.72, whiteSpace: 'nowrap', fontWeight: 600 }}>{date.date}</span>
              </Outbound>
            );
          })}
        </div>
      );
    }
    case 'newsletter':
      return null;
    default:
      return null;
  }
}

function CoverHero({
  page, theme, headerBlock, coverBlock, handleBlock, socialBlock, onLinkClick,
}: {
  page: LinkTreePage;
  theme: LinkTreeTheme;
  headerBlock?: LinkTreeBlock;
  coverBlock?: LinkTreeBlock;
  handleBlock?: LinkTreeBlock;
  socialBlock?: LinkTreeBlock;
  onLinkClick?: (block: LinkTreeBlock, url: string) => void;
}) {
  const header = headerBlock?.data || {};
  const cover = coverBlock?.data || {};
  const name = page.title || header.name || cover.title || 'Artist';
  const tagline = page.bio || header.tagline || cover.tagline || cover.subtitle || '';
  const avatar = safeUrl(page.avatar_url || header.avatar || '');
  const coverUrl = safeUrl(page.cover_url || cover.image || cover.cover || '');
  const accent = theme.secondary || theme.accent || theme.primary;
  const avatarRing = safeCss(theme.avatarRing, `conic-gradient(from 140deg, ${theme.primary}, ${accent}, ${theme.primary})`);
  const overlay = safeCss(theme.heroOverlay, 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.48) 100%)');
  const tint = safeCss(theme.heroTint, `linear-gradient(135deg, ${hexToRgba(theme.primary, 0.2)}, transparent 58%, ${hexToRgba(accent, 0.18)})`);
  const verifiedBlue = 'hsl(204 100% 48%)';

  return (
    <section style={{ textAlign: 'center', paddingTop: coverUrl ? 0 : 26, position: 'relative', zIndex: 0 }}>
      {coverUrl && (
        <div style={{ position: 'relative', zIndex: 1, height: 178, borderRadius: 24, overflow: 'hidden', background: `url("${coverUrl}") center/cover no-repeat` }}>
          {tint !== 'transparent' && <div aria-hidden style={{ position: 'absolute', inset: 0, background: tint, mixBlendMode: 'overlay' }} />}
          <div aria-hidden style={{ position: 'absolute', inset: 0, background: overlay }} />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: coverUrl ? -62 : 0, position: 'relative', zIndex: 2 }}>
        <div style={{ padding: 3, borderRadius: '50%', background: avatarRing, boxShadow: `0 18px 48px -18px ${hexToRgba(theme.primary, 0.72)}`, position: 'relative', zIndex: 2 }}>
          {avatar ? (
            <img
              src={avatar}
              alt={name}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              onError={event => { event.currentTarget.style.display = 'none'; }}
              style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', display: 'block', border: `4px solid ${theme.cardBorder || 'rgba(255,255,255,0.2)'}`, background: theme.cardBg || 'rgba(255,255,255,0.10)' }}
            />
          ) : (
            <div style={{
              width: 120, height: 120, borderRadius: '50%', display: 'grid', placeItems: 'center',
              background: theme.cardBg || 'rgba(255,255,255,0.12)', border: `4px solid ${theme.cardBorder || 'rgba(255,255,255,0.24)'}`,
              color: theme.text, fontSize: 48, fontWeight: 800, textShadow: '0 2px 12px rgba(0,0,0,0.3)',
            }}>{name.charAt(0).toUpperCase()}</div>
          )}
        </div>
      </div>
      <div style={{ padding: '17px 12px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <h1 style={{ color: theme.text, fontSize: 25, lineHeight: 1.16, margin: 0, fontWeight: 800, letterSpacing: 0 }}>{name}</h1>
          {theme.showVerified && (theme.verifiedBadgeStyle === 'instagram' ? (
            <span aria-label="Verified" style={{ width: 20, height: 20, borderRadius: '50%', background: verifiedBlue, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Check size={13} color="#fff" strokeWidth={3} />
            </span>
          ) : (
            <BadgeCheck aria-label="Verified" size={21} style={{ color: theme.primary, flexShrink: 0 }} />
          ))}
        </div>
        {tagline && <p style={{ color: theme.mutedText || theme.text, opacity: theme.mutedText ? 1 : 0.78, margin: '8px auto 0', maxWidth: 370, fontSize: 14, lineHeight: 1.5 }}>{tagline}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          {handleBlock && <HandlePill block={handleBlock} theme={theme} onLinkClick={onLinkClick} />}
          {socialBlock && <SocialRow block={socialBlock} theme={theme} onLinkClick={onLinkClick} />}
        </div>
      </div>
    </section>
  );
}

export default function PublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<LinkTreePage | null>(null);
  const [blocks, setBlocks] = useState<LinkTreeBlock[]>([]);
  const [pixels, setPixels] = useState<LinkTreePixel[]>([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) { setNotFound(true); return; }
    let cancelled = false;
    (async () => {
      const slugLower = slug.toLowerCase();
      // Fetch without status filter so we can also detect soft-deleted rows
      // and redirect their visitors to santomusic.io. RLS allows anon SELECT
      // on live rows AND on soft-deleted rows for exactly this reason.
      const { data: p, error: pErr } = await supabase
        .from('linktree_pages').select('*')
        .eq('slug', slugLower).maybeSingle();
      if (cancelled) return;
      if (pErr) {
        // Distinguish a true backend error (RLS denial, network, 500) from
        // simply "no row matches" — maybeSingle returns null for the latter.
        // eslint-disable-next-line no-console
        console.error('[linksanto] page fetch error', pErr);
        setNotFound(true);
        return;
      }
      if (!p) { setNotFound(true); return; }
      // Page deleted by owner OR account scheduled for deletion → redirect.
      if ((p as any).deleted_at || (p as any).owner_deleted_at) {
        window.location.replace('https://santomusic.io');
        return;
      }
      if (p.status !== 'live') { setNotFound(true); return; }
      setPage(p as any);

      const [{ data: bs }, { data: px }] = await Promise.all([
        supabase.from('linktree_blocks').select('*').eq('page_id', p.id).eq('is_visible', true).order('position', { ascending: true }),
        supabase.from('linktree_pixels').select('*').eq('page_id', p.id).eq('is_active', true),
      ]);
      if (cancelled) return;
      setBlocks((bs ?? []) as any);
      setPixels((px ?? []) as any);

      trackPageView(p.id);

      const seo = (p.seo as any) || {};
      const shareTitle = seo.title || p.title || `@${slug} on LinkSanto`;
      const shareDesc = seo.description || p.bio || '';
      const shareImage = safeUrl(seo.og_image || seo.ogImage) || safeUrl(p.cover_url) || safeUrl(p.avatar_url) || '';
      const shareUrl = window.location.href.split('?')[0];
      const themeBg = (p.theme as any)?.bg;
      const themeColor = (typeof themeBg === 'string' && /^#[0-9a-f]{3,6}$/i.test(themeBg)) ? themeBg : '#0a0a0a';

      document.title = shareTitle;
      const setMeta = (name: string, content: string) => {
        if (!content) return;
        let m = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
        if (!m) { m = document.createElement('meta'); m.setAttribute('name', name); document.head.appendChild(m); }
        m.content = content;
      };
      const setOg = (prop: string, content: string) => {
        if (!content) return;
        let m = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement;
        if (!m) { m = document.createElement('meta'); m.setAttribute('property', prop); document.head.appendChild(m); }
        m.content = content;
      };
      setMeta('description', shareDesc);
      setMeta('theme-color', themeColor);
      setOg('og:title', shareTitle);
      setOg('og:description', shareDesc);
      setOg('og:type', 'profile');
      setOg('og:url', shareUrl);
      setOg('og:site_name', 'LinkSanto');
      if (shareImage) setOg('og:image', shareImage);
      setMeta('twitter:card', shareImage ? 'summary_large_image' : 'summary');
      setMeta('twitter:title', shareTitle);
      setMeta('twitter:description', shareDesc);
      if (shareImage) setMeta('twitter:image', shareImage);

      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = shareUrl;
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    // Guard: pixels must only fire on the public LinkSanto domain.
    const PIXEL_ALLOWED = ['me.linksanto.io', 'staging.me.linksanto.io'];
    if (!PIXEL_ALLOWED.includes(window.location.hostname)) return;

    const addedScripts: HTMLElement[] = [];
    const addScript = (src: string): HTMLScriptElement => {
      const s = document.createElement('script');
      s.async = true; s.src = src; s.dataset.linktreePixel = '1';
      document.head.appendChild(s);
      addedScripts.push(s);
      return s;
    };

    pixels.forEach(px => {
      const key = `${px.provider}:${px.pixel_id}`;
      if (initializedPixels.has(key)) {
        const w = window as any;
        try {
          if (px.provider === 'fb' && typeof w.fbq === 'function') {
            w.fbq('init', px.pixel_id);
            w.fbq('trackSingle', px.pixel_id, 'PageView');
          } else if (px.provider === 'tiktok' && w.ttq) {
            const inst = typeof w.ttq.instance === 'function' ? w.ttq.instance(px.pixel_id) : w.ttq;
            inst.page?.();
          } else if (px.provider === 'google' && typeof w.gtag === 'function') {
            w.gtag('config', px.pixel_id, { page_path: window.location.pathname });
          } else if (px.provider === 'gtm') {
            const dlName = gtmDataLayerName(px.pixel_id);
            const dl = w[dlName];
            if (Array.isArray(dl)) dl.push({ event: 'page_view', page_path: window.location.pathname, linktree_pixel_id: px.pixel_id });
          }
        } catch { /* analytics is best-effort */ }
        return;
      }
      markPixelInitialized(key);
      try {
        if (px.provider === 'fb') {
          const w = window as any;
          if (!w.fbq) {
            (function (f: any, b: Document, e: string, v: string) {
              if (f.fbq) return; const n: any = f.fbq = function () {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
              };
              if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
              const t = b.createElement(e) as HTMLScriptElement; t.async = true; t.src = v; t.dataset.linktreePixel = '1';
              const s = b.getElementsByTagName(e)[0]; s.parentNode!.insertBefore(t, s);
              addedScripts.push(t);
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
          }
          w.fbq('init', px.pixel_id);
          w.fbq('trackSingle', px.pixel_id, 'PageView');
        } else if (px.provider === 'google') {
          addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(px.pixel_id)}`);
          const w = window as any;
          w.dataLayer = w.dataLayer || [];
          if (!w.gtag) { w.gtag = function () { w.dataLayer.push(arguments); }; }
          w.gtag('js', new Date());
          w.gtag('config', px.pixel_id);
        } else if (px.provider === 'tiktok') {
          const w = window as any;
          if (!w.ttq) {
            (function (w: any, d: Document, t: string) {
              w.TiktokAnalyticsObject = t;
              const ttq: any = w[t] = w[t] || [];
              ttq.methods = ['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];
              ttq.setAndDefer = function (target: any, method: string) {
                target[method] = function () { target.push([method].concat(Array.prototype.slice.call(arguments, 0))); };
              };
              for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
              ttq.instance = function (pid: string) {
                const inst = ttq._i[pid] || [];
                for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(inst, ttq.methods[n]);
                return inst;
              };
              ttq.load = function (e: string) {
                const src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
                ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = src;
                ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
                ttq._o = ttq._o || {}; ttq._o[e] = {};
                const o = d.createElement('script') as HTMLScriptElement;
                o.type = 'text/javascript'; o.async = true; o.src = `${src}?sdkid=${encodeURIComponent(e)}&lib=${encodeURIComponent(t)}`;
                o.dataset.linktreePixel = '1';
                const a = d.getElementsByTagName('script')[0];
                a.parentNode!.insertBefore(o, a);
                addedScripts.push(o);
              };
            })(window, document, 'ttq');
          }
          w.ttq.load(px.pixel_id);
          const inst = typeof w.ttq.instance === 'function' ? w.ttq.instance(px.pixel_id) : w.ttq;
          inst.page?.();
        } else if (px.provider === 'gtm') {
          const w = window as any;
          const dlName = gtmDataLayerName(px.pixel_id);
          w[dlName] = w[dlName] || [];
          w[dlName].push({ 'gtm.start': Date.now(), event: 'gtm.js' });
          addScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(px.pixel_id)}&l=${encodeURIComponent(dlName)}`);
        }
      } catch { /* analytics is best-effort */ }
    });

    return () => {
      addedScripts.forEach(s => { try { s.parentNode?.removeChild(s); } catch { /* noop */ } });
    };
  }, [pixels]);

  const theme: LinkTreeTheme | null = useMemo(() => {
    if (!page) return null;
    const templateId = (page.theme as any)?.templateId;
    const baseTheme = getArtistTemplate(templateId)?.palette || getTemplate(templateId).theme;
    return { ...baseTheme, ...(page.theme as any) };
  }, [page]);

  const pageBg = theme ? safeCss(theme.bg, '#0a0a0a') : '#0a0a0a';

  useEffect(() => {
    if (!theme) return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const previous = {
      htmlBg: html.style.background,
      htmlMinHeight: html.style.minHeight,
      bodyBg: body.style.background,
      bodyColor: body.style.color,
      bodyMinHeight: body.style.minHeight,
      bodyBgAttachment: body.style.backgroundAttachment,
      rootBg: root?.style.background,
      rootMinHeight: root?.style.minHeight,
    };
    html.style.background = pageBg;
    html.style.minHeight = '-webkit-fill-available';
    body.style.background = pageBg;
    body.style.backgroundAttachment = 'fixed';
    body.style.color = theme.text;
    // min-height with progressive fallbacks
    body.style.minHeight = '100vh';
    body.style.minHeight = '-webkit-fill-available';
    body.style.minHeight = '100svh';
    if (root) {
      root.style.background = pageBg;
      root.style.minHeight = '100svh';
    }
    return () => {
      html.style.background = previous.htmlBg;
      html.style.minHeight = previous.htmlMinHeight || '';
      body.style.background = previous.bodyBg;
      body.style.backgroundAttachment = previous.bodyBgAttachment || '';
      body.style.color = previous.bodyColor;
      body.style.minHeight = previous.bodyMinHeight || '';
      if (root) {
        root.style.background = previous.rootBg || '';
        root.style.minHeight = previous.rootMinHeight || '';
      }
    };
  }, [pageBg, theme]);

  if (notFound) {
    return (
      <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0a0a0a', color: '#fff', padding: 24, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>🎧</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>This page isn't live</h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>
            The artist link you're looking for doesn't exist or has been taken offline.
          </p>
          <a href="https://linksanto.io" style={{ display: 'inline-block', marginTop: 20, padding: '10px 18px', borderRadius: 999, background: '#8B5CF6', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
            Build yours on LinkSanto
          </a>
        </div>
      </main>
    );
  }

  if (!page || !theme) {
    return <main aria-busy="true" aria-label="Loading page" style={{ minHeight: '100vh', width: '100%', background: '#0a0a0a' }} />;
  }

  const onLinkClick = (block: LinkTreeBlock, url: string) => {
    const safe = safeUrl(url);
    if (!safe) return;
    trackLinkClick(page.id, block.id, safe);
    firePixelEvents(block.type === 'social_row' ? 'social_click' : 'link_click', pixels, {
      label: block.data?.label || block.data?.title || block.data?.handle || block.type,
      url: safe,
      blockId: block.id,
      pageId: page.id,
      category: block.type === 'merch' ? 'merch' : 'outbound',
    });
  };

  const visibleBlocks = blocks
    .filter(block => block.is_visible !== false)
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const showHero = !theme.hideHero;
  const headerBlock = showHero ? visibleBlocks.find(block => block.type === 'header') : undefined;
  const coverBlock = showHero ? visibleBlocks.find(block => block.type === 'cover_hero') : undefined;
  const handleBlock = showHero ? visibleBlocks.find(block => block.type === 'handle') : undefined;
  const socialBlock = showHero ? visibleBlocks.find(block => block.type === 'social_row') : undefined;
  const remainingBlocks = showHero
    ? visibleBlocks.filter(block => !['header', 'cover_hero', 'handle', 'social_row'].includes(block.type))
    : visibleBlocks;

  return (
    <main
      className="min-h-screen w-full"
      style={{
        minHeight: '100svh', width: '100%', margin: 0,
        background: pageBg, backgroundAttachment: 'fixed', backgroundSize: 'cover',
        color: theme.text, fontFamily: fontFamily(theme.font), overflowX: 'hidden',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', padding: '30px 18px 24px', boxSizing: 'border-box' }}>
        {showHero && (
          <CoverHero
            page={page}
            theme={theme}
            headerBlock={headerBlock}
            coverBlock={coverBlock}
            handleBlock={handleBlock}
            socialBlock={socialBlock}
            onLinkClick={onLinkClick}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: showHero ? 24 : 0 }}>
          {remainingBlocks.map(block => (
            <PublicBlock key={block.id} block={block} theme={theme} onLinkClick={onLinkClick} />
          ))}
        </div>

        <footer style={{ textAlign: 'center', padding: '26px 0 10px', color: theme.mutedText || theme.text, opacity: theme.mutedText ? 1 : 0.58, fontSize: 11, letterSpacing: 0 }}>
          <a
            href="https://linksanto.io/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Powered by linksanto.io — opens linksanto.io in a new tab"
            style={{ color: 'inherit', textDecoration: 'none', padding: '6px 10px', display: 'inline-block' }}
          >
            Powered by <span style={{ fontWeight: 700 }}>linksanto.io</span>
          </a>
        </footer>
      </div>
    </main>
  );
}
